import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Shared navigation-state mechanism (2026-09 P4) — encodes a page's
 * tab/filter/sort/page/scope state in the URL's own query string instead
 * of component-local state, so leaving the page (e.g. opening a customer)
 * and returning via the browser's own Back button restores exactly what
 * was selected. One implementation, reused by Tasks/Calls/Consultations/
 * Pipelines — never a bespoke per-page mechanism.
 *
 * `defaults` — {key: defaultValue}. A value equal to its default is
 * omitted from the URL entirely, so a page's "clean" URL stays short and
 * only genuinely non-default filters ever show up in the address bar.
 * Values round-trip as strings; numeric defaults are parsed back to
 * numbers automatically.
 *
 * Returns [state, setState] — state is always fully populated (defaults
 * filled in), setState(partial) merges and replaces the current history
 * entry (never pushes a new one — a filter tweak is not a "page" for
 * Back-button purposes; only real navigation to a different route is).
 */
export function useUrlState(defaults) {
  const [searchParams, setSearchParams] = useSearchParams();

  const state = useMemo(() => {
    const out = {};
    for (const key of Object.keys(defaults)) {
      const raw = searchParams.get(key);
      if (raw === null) {
        out[key] = defaults[key];
      } else if (typeof defaults[key] === "number") {
        const n = Number(raw);
        out[key] = Number.isFinite(n) ? n : defaults[key];
      } else if (typeof defaults[key] === "boolean") {
        out[key] = raw === "true";
      } else {
        out[key] = raw;
      }
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.toString()]);

  const setState = useCallback((partial) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      const resolved = typeof partial === "function" ? partial(state) : partial;
      for (const [key, value] of Object.entries(resolved)) {
        const isDefault = value === undefined || value === null || value === ""
          || value === defaults[key];
        if (isDefault) next.delete(key);
        else next.set(key, String(value));
      }
      return next;
    }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  return [state, setState];
}

// A safe, internal-only "return to" helper — never accepts/returns an
// absolute or external URL. Used by task-launched actions (P4 Problem 10)
// so a Customer 360 visit opened from a Tasks row can return to that
// exact Tasks URL afterward, without ever becoming an open-redirect.
export function safeInternalPath(path) {
  if (typeof path !== "string" || !path.startsWith("/") || path.startsWith("//")) {
    return null;
  }
  return path;
}
